import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Footer, Header } from './headerFooter';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import ListGroup from 'react-bootstrap/ListGroup';
import { EIDIKI_TABLE_OPTIONS } from './constants';
import { getHost } from './config';
import FilterSidebar from './FilterSidebar';
import {
    initialSidebarOpen,
    collapseFilterSidebarOnMobile,
    formatYearRangeLabel,
    joinFilterSummaryParts,
} from './filterSidebarUtils';
import 'bootstrap/dist/css/bootstrap.min.css';
import "./custom.scss";

function AppointmentDate() {
    const [minDate, setMinDate] = useState(null);
    const [ranking, setRanking] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();
    const [yearRange, setYearRange] = useState(''); // New state for year range
    const [specialty, setSpecialty] = useState(''); // New state for specialty
    const [eidikiAgogiTableType, setEidikiAgogiTableType] = useState('')
    const [specialtyOptions, setSpecialtyOptions] = useState([]); // Options for specialty dropdown
    const [specialtyLoading, setSpecialtyLoading] = useState(false); // Loading state for specialty fetch
    const [loading, setLoading] = useState(false); // New loading state
    const [sidebarOpen, setSidebarOpen] = useState(initialSidebarOpen);
    const token = localStorage.getItem('authToken');

    useEffect(() => {
        if (!token) {
            navigate('/', { replace: true });
        }
    }, [token, navigate]);

    useEffect(() => {
        if (yearRange) { // Fetch only if yearRange is selected
            setSpecialtyLoading(true);
            const url = `${getHost()}/api/specialty-types?year_range=${yearRange}`;
            fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            })
                .then(response => response.json())
                .then(data => setSpecialtyOptions(data.results))
                .catch(error => console.error('Error fetching specialties:', error))
                .finally(() => setSpecialtyLoading(false));
        } else {
            setSpecialtyOptions([]); // Clear specialties when yearRange is not selected
        }
    }, [yearRange]);


    
    const handleSubmit = () => {
        setLoading(true);
        const url = `${getHost()}/api/insert-date`
        const requestBody = {
            ranking: ranking,
            year_range: yearRange,
            specialty: specialty,
            eidiki_agogi_table_type: eidikiAgogiTableType

        };
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(requestBody),

        })
            .then(response => response.json())
            .then(data => {
                if (data.results && Object.keys(data.results).length > 0) {
                    const { min_date } = data.results;
                    if (min_date === -1) {
                        setErrorMessage('Δεν υπάρχουν αποτελέσματα');
                    } else {
                        setMinDate(min_date);
                        setShowResults(true);
                        setErrorMessage('');
                        collapseFilterSidebarOnMobile(setSidebarOpen);
                    }
                } else {
                    setErrorMessage('Δεν υπάρχουν αποτελέσματα');
                }
            })
            .catch(error => console.error('Error submitting data:', error))
            .finally(() => {
                setLoading(false); // Hide loading indicator regardless of success/failure
            });
    };

    const handleLogout = () => {
        // Clear the authToken from localStorage
        localStorage.removeItem('authToken');
        localStorage.setItem('logout-event', Date.now());

        // Redirect to the login page
        navigate('/', { replace: true });
    };

    const isFormValid = yearRange !== '' && specialty !== '' && ranking !=='' && eidikiAgogiTableType !=='';

    const filterSummary = useMemo(
        () => joinFilterSummaryParts([
            formatYearRangeLabel(yearRange),
            specialty,
            ranking ? `θέση ${ranking}` : '',
        ]),
        [yearRange, specialty, ranking]
    );

    return (
        <div id="root">
            <Header handleLogout={handleLogout}/>
            <main className='main-container ranking-per-state-page'>
                <FilterSidebar
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    filterSummary={filterSummary}
                    bodyId="rest-components-sidebar-body"
                >
                        <Form>
                    <Form.Group className="mb-3" controlId="yearSelect.range">
                        <Form.Label className='custom-label'>Έτος:</Form.Label>
                        <Form.Select
                            className="custom-form" 
                            value={yearRange} 
                            onChange={(e) => {
                                setYearRange(e.target.value);
                                setSpecialty("");
                            }}>
                            <option>Επιλέξτε...</option>
                            <option value="2022_2023">2022-2023</option>
                            <option value="2023_2024">2023-2024</option>
                            <option value="2024_2025">2024-2025</option>
                            <option value="2025_2026">2025-2026</option>
                        </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3" controlId="yearSelect.specialty">
                        <Form.Label className='custom-label'>Ειδικότητα:</Form.Label>
                        <Form.Select className="custom-form" value={specialty} onChange={(e) => setSpecialty(e.target.value)} disabled={!yearRange || specialtyLoading}>
                        <option value="">{specialtyLoading ? 'Φόρτωση...' : 'Επιλέξτε...'}</option>
                            {!specialtyLoading && (specialtyOptions.length > 0 ? (
                                specialtyOptions
                                .slice() // Create a shallow copy of the array to avoid mutating the original
                                .sort((a, b) => a.localeCompare(b)) // Sort alphabetically
                                .map((spec) => (
                                    <option key={spec} value={spec}>
                                        {spec}
                                    </option>
                                ))
                            ) : (
                                <option value="">No specialties available</option>
                            ))}
                        </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                        <Form.Label className='custom-label'>Θέση πίνακα:</Form.Label>
                        <Form.Control className="custom-form" type="text" placeholder="Εισάγετε την θέση σας στον πίνακα" name="id_card_number" value={ranking} onChange={(e) => setRanking(e.target.value.trimEnd())} />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label className='custom-label'>Είδος πίνακα:</Form.Label>
                    <Form.Select className="custom-form" name="table_value" value={eidikiAgogiTableType} onChange={(e) => setEidikiAgogiTableType(e.target.value)} required>
                        <option value="">Επιλέξτε πίνακα</option>
                        {EIDIKI_TABLE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option> ))}
                    </Form.Select>
                </Form.Group>
                <p className="text-muted fst-italic small pt-2">
                        * Η θέση που καταχωρείτε μπορεί να προέρχεται από τον:<br/>
                        <ul>
                            <li><b>Κύριο</b> πίνακα ειδικής αγωγής (απόφοιτοι ΑΕΙ στην ΕΑΕ, κάτοχοι διδακτορικών & μεταπτυχιακών στην ΕΑΕ)</li>
                            <li><b>Επικουρικό</b> πίνακα ειδικής αγωγής (σεμινάριο/ εξειδίκευση στην ΕΑΕ 400 ωρών, προϋπηρεσία τουλάχιστον 10 μηνών στην ΕΑΕ)</li>
                            <li>πίνακα <b>Γενικής</b> αγωγής (λοιποί, κατόπιν εξάντλησης των παραπάνω)</li>
                        </ul>
                    </p>
                        </Form>
                        <p className="ranking-per-state-sidebar-note text-muted fst-italic small">
                            * Η θέση που καταχωρείτε μπορεί να προέρχεται από τον:<br/>
                            <ul>
                                <li><b>Κύριο</b> πίνακα ειδικής αγωγής (απόφοιτοι ΑΕΙ στην ΕΑΕ, κάτοχοι διδακτορικών & μεταπτυχιακών στην ΕΑΕ)</li>
                                <li><b>Επικουρικό</b> πίνακα ειδικής αγωγής (σεμινάριο/ εξειδίκευση στην ΕΑΕ 400 ωρών, προϋπηρεσία τουλάχιστον 10 μηνών στην ΕΑΕ)</li>
                                <li>πίνακα <b>Γενικής</b> αγωγής (λοιποί, κατόπιν εξάντλησης των παραπάνω)</li>
                            </ul>
                        </p>
                        <p className="ranking-per-state-sidebar-note text-muted fst-italic small">
                            ** <strong>Διευκρίνιση: </strong>Ο εκπαιδευτικός θα πρέπει να εισάγει τη θέση βάσει του αρχικού πίνακα και όχι των εκκαθαρισμένων.
                        </p>
                        <p className="mb-5">
                            <Button variant="secondary" className="w-100 mt-3" type="button" onClick={handleSubmit} disabled={!isFormValid} style={{ display: 'block', visibility: 'visible' }}>Υποβολή</Button>
                        </p>
                </FilterSidebar>
                <div className={`ranking-per-state-main ${!sidebarOpen ? 'ranking-per-state-main--sidebar-collapsed' : ''}`}>
                    <Container fluid className="ranking-per-state-container">
                        <Row className="g-3 g-md-4">
                            <Col xs={12}>
                                <div className="ranking-per-state-content">
                                {!showResults && !errorMessage && !loading && (
                                    <p className="text-muted small mb-2">
                                        Ο εκπαιδευτικός εισάγει την <strong>σχολική χρονιά</strong> που τον ενδιαφέρει, την <strong>ειδικότητά</strong> του και την <strong>θέση</strong> του στον αρχικό πίνακα (όχι εκκαθαρισμένο) και ενημερώνεται σχετικά με την ημερομηνία πρόσληψης.<br />
                                        Πρόκειται για ένα εργαλείο που απευθύνεται κατά βάση σε αναπληρωτές εκπαιδευτικούς που δεν έχουν εργαστεί στο παρελθόν και θα ήθελαν να έχουν μία εκτίμηση σχετικά με την ενδεχόμενη ημερομηνία προσληψής τους, λαμβάνοντας υπόψη ιστορικά δεδομένα.
                                    </p>
                                )}
                                {loading && (
                                    <div className="text-center mt-3 mb-3">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <div>Φόρτωση αποτελεσμάτων...</div>
                                    </div>
                                )}

                                {errorMessage || showResults ? (
                                    <div className={!sidebarOpen ? 'results-snug-mobile' : ''}>
                                        <div className="data-container">
                                            {errorMessage && (
                                                <Card className='custom-card'>
                                                    <Card.Body>
                                                        <Card.Title>{errorMessage}</Card.Title>
                                                    </Card.Body>
                                                </Card>
                                            )}
                                            {showResults && !errorMessage && (
                                                <Card className='custom-card' key="results">
                                                    <Card.Body>
                                                        <Card.Title>Ημερομηνία πρόσληψης</Card.Title>
                                                        {minDate ? (
                                                            <ListGroup className="list-group-flush">
                                                                <ListGroup.Item>Έγινε πρόσληψη στις: {minDate}</ListGroup.Item>
                                                            </ListGroup>
                                                        ) : (
                                                            <p>Δεν υπάρχουν αποτελέσματα</p>
                                                        )}
                                                    </Card.Body>
                                                </Card>
                                            )}
                                        </div>
                                    </div>
                                ) : !loading && null}
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </main>
            <Footer />
        </div>
    );
}

export default AppointmentDate;